import { AnimatePresence, motion } from "framer-motion";
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiBarChart2Line,
  RiChat3Line,
  RiCloseLine,
  RiGroup3Line,
  RiLayoutGridLine,
  RiLink,
  RiMenu3Line,
  RiShoppingCart2Line,
  RiSparkling2Line,
  RiTShirt2Line,
  RiRestaurantLine,
  RiScissorsCutLine,
  RiTeamLine,
} from "@remixicon/react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useAppSession } from "@/hooks/useAppSession";
import { APP_LOGIN_URL, APP_REGISTER_URL } from "@/lib/site";
import { cn } from "@/lib/utils";

import Logo from "@/assets/logo.png";
import { menuEase } from "./marketing-motion";
import { StorePreviewMock } from "./StorePreviewMock";

type MegaFeature = {
  to: string;
  title: string;
  description: string;
};

type MegaCategory = {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  features: MegaFeature[];
};

const productCategories: MegaCategory[] = [
  {
    id: "sales-link",
    label: "Your sales link",
    Icon: RiLink,
    features: [
      {
        to: "/product/sales-link",
        title: "Sales page",
        description: "sqaleshop.com/yourbrand, share one link everywhere",
      },
      {
        to: "/product/sales-link",
        title: "Custom appearance",
        description: "Brand colors, logo, and layout that feel like you",
      },
      {
        to: "/product/sales-link",
        title: "Share anywhere",
        description: "Bio, stories, WhatsApp, paste your link and sell",
      },
    ],
  },
  {
    id: "sell",
    label: "Sell & checkout",
    Icon: RiShoppingCart2Line,
    features: [
      {
        to: "/product/sell",
        title: "Products & variants",
        description: "Photos, prices, sizes, and availability",
      },
      {
        to: "/product/sell",
        title: "Checkout & payments",
        description: "Order details and payment in one flow",
      },
      {
        to: "/product/sell",
        title: "Order dashboard",
        description: "Every order in one place, not buried in chat",
      },
    ],
  },
  {
    id: "grow",
    label: "Grow with insight",
    Icon: RiBarChart2Line,
    features: [
      {
        to: "/product/grow",
        title: "Sales analytics",
        description: "Visits → product views → orders → revenue",
      },
      {
        to: "/product/grow",
        title: "Customer insights",
        description: "Who bought what, how often, total spent",
      },
      {
        to: "/product/grow",
        title: "Sqaleshop AI",
        description: "Smart recommendations in your analytics tab",
      },
    ],
  },
  {
    id: "connect",
    label: "Stay connected",
    Icon: RiChat3Line,
    features: [
      {
        to: "/product/connect",
        title: "WhatsApp integration",
        description: "Let buyers talk before they purchase",
      },
      {
        to: "/product/connect",
        title: "Customer database",
        description: "Repeat buyers and order history at a glance",
      },
      {
        to: "/product/connect",
        title: "Remarketing tools",
        description: "Abandoned orders and repeat customer outreach",
      },
    ],
  },
];

type SolutionCategory = {
  id: string;
  to: string;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  features: MegaFeature[];
};

const solutionsMenu: SolutionCategory[] = [
  {
    id: "fashion-lifestyle",
    to: "/solutions/fashion-lifestyle",
    label: "Fashion & lifestyle",
    description: "Sizes, drops, and restocks without the DMs",
    Icon: RiTShirt2Line,
    features: [
      {
        to: "/solutions/fashion-lifestyle",
        title: "Products & variants",
        description:
          "Sizes and colors so shoppers self-serve before they ping you",
      },
      {
        to: "/solutions/fashion-lifestyle",
        title: "One storefront link",
        description: "Bio, stories, and campaigns share the same page",
      },
      {
        to: "/solutions/fashion-lifestyle",
        title: "Repeat customers",
        description: "Order history so restocks and exchanges are easy",
      },
    ],
  },
  {
    id: "food-beverage",
    to: "/solutions/food-beverage",
    label: "Food & beverage",
    description: "Menus and orders before the first message",
    Icon: RiRestaurantLine,
    features: [
      {
        to: "/solutions/food-beverage",
        title: "Menus & bundles",
        description: "Dishes, packs, and add-ons customers choose first",
      },
      {
        to: "/solutions/food-beverage",
        title: "One order queue",
        description: "Peak hours without duplicate DMs and wrong items",
      },
      {
        to: "/solutions/food-beverage",
        title: "Pickup windows",
        description: "Slots when you offer timed pickup or tastings",
      },
    ],
  },
  {
    id: "services-bookings",
    to: "/solutions/services-bookings",
    label: "Services & bookings",
    description: "Packages and time slots in one link",
    Icon: RiScissorsCutLine,
    features: [
      {
        to: "/solutions/services-bookings",
        title: "Packages & slots",
        description: "Services and availability in one place",
      },
      {
        to: "/solutions/services-bookings",
        title: "Client history",
        description: "Who booked what, deposits, and follow-ups",
      },
      {
        to: "/solutions/services-bookings",
        title: "Less calendar ping-pong",
        description: "Inquiry to paid session without the back-and-forth",
      },
    ],
  },
  {
    id: "social-sellers",
    to: "/solutions/social-sellers",
    label: "Social sellers",
    description: "Built for Instagram and WhatsApp sellers",
    Icon: RiTeamLine,
    features: [
      {
        to: "/solutions/social-sellers",
        title: "One sales link",
        description: "Bio, status, and WhatsApp, paste and sell",
      },
      {
        to: "/solutions/social-sellers",
        title: "Orders in a dashboard",
        description: "Not buried in chat threads",
      },
      {
        to: "/solutions/social-sellers",
        title: "Built for DMs",
        description: "A professional storefront that still feels like you",
      },
    ],
  },
];

type MenuKey = "product" | "solutions";

const panelVariants = {
  hidden: {
    opacity: 0,
    scale: 0.97,
    y: -8,
    filter: "blur(4px)",
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.24, ease: menuEase },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    y: -4,
    filter: "blur(2px)",
    transition: { duration: 0.14, ease: menuEase },
  },
} as const;

function ProductMegaMenu({ onNavigate }: { onNavigate: () => void }) {
  const [activeCategory, setActiveCategory] = useState(productCategories[0].id);
  const category =
    productCategories.find((c) => c.id === activeCategory) ??
    productCategories[0];

  return (
    <motion.div
      className="absolute top-full right-0 left-0 z-10 pt-3 max-w-[80%] mx-auto"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={panelVariants}
    >
      <div className="overflow-hidden rounded-3xl border border-border bg-card p-4 ring-1 ring-black/[0.03] backdrop-blur-xl sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          {/* Category sidebar */}
          <div className="space-y-1">
            {productCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onMouseEnter={() => setActiveCategory(cat.id)}
                onFocus={() => setActiveCategory(cat.id)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-3 text-left text-sm font-medium transition-colors",
                  activeCategory === cat.id
                    ? "bg-muted text-foreground"
                    : "text-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                <span className="flex items-center gap-2 text-base font-semibold">
                  <cat.Icon className="size-4 shrink-0" />
                  {cat.label}
                </span>
                <RiArrowRightSLine className="size-4 shrink-0 opacity-50" />
              </button>
            ))}
          </div>

          {/* Features list */}
          <div className="space-y-1 border-l border-border pl-4">
            {category.features.map((feature) => (
              <Link
                key={feature.title}
                to={feature.to}
                onClick={onNavigate}
                className="group block rounded-2xl px-3 py-3 transition-colors hover:bg-muted/60"
              >
                <span className="block text-sm font-semibold text-foreground group-hover:text-primary">
                  {feature.title}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                  {feature.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function SolutionsMegaMenu({ onNavigate }: { onNavigate: () => void }) {
  const [activeCategory, setActiveCategory] = useState(solutionsMenu[0].id);
  const category =
    solutionsMenu.find((c) => c.id === activeCategory) ?? solutionsMenu[0];

  return (
    <motion.div
      className="absolute top-full right-0 left-0 z-10 mx-auto max-w-[80%] pt-3"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={panelVariants}
    >
      <div className="overflow-hidden rounded-3xl border border-border bg-card p-4 ring-1 ring-black/[0.03] backdrop-blur-xl sm:p-5">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div className="space-y-1">
            {solutionsMenu.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onMouseEnter={() => setActiveCategory(cat.id)}
                onFocus={() => setActiveCategory(cat.id)}
                className={cn(
                  "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-3 text-left text-sm font-medium transition-colors",
                  activeCategory === cat.id
                    ? "bg-muted text-foreground"
                    : "text-foreground hover:bg-muted/60 hover:text-foreground",
                )}
              >
                <span className="flex items-center gap-2 text-base font-semibold">
                  <cat.Icon className="size-4 shrink-0" />
                  {cat.label}
                </span>
                <RiArrowRightSLine className="size-4 shrink-0 opacity-50" />
              </button>
            ))}
          </div>

          <div className="space-y-1 border-l border-border pl-4">
            {category.features.map((feature) => (
              <Link
                key={feature.title}
                to={feature.to}
                onClick={onNavigate}
                className="group block rounded-2xl px-3 py-3 transition-colors hover:bg-muted/60"
              >
                <span className="block text-sm font-semibold text-foreground group-hover:text-primary">
                  {feature.title}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                  {feature.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function NavTrigger({
  label,
  open,
  onHover,
}: {
  label: string;
  open: boolean;
  onHover: () => void;
}) {
  return (
    <button
      type="button"
      onMouseEnter={onHover}
      onFocus={onHover}
      className={cn(
        "relative inline-flex items-center gap-0.5 rounded-lg px-4 py-2 text-base font-semibold tracking-tight transition-all duration-200",
        open
          ? "bg-muted text-foreground"
          : "text-foreground/90 hover:bg-muted/60 hover:text-foreground",
      )}
      aria-expanded={open}
    >
      {label}
    </button>
  );
}

function HeaderAuthActions({
  variant,
  onNavigate,
}: {
  variant: "desktop" | "mobile";
  onNavigate?: () => void;
}) {
  const { signedIn, isLoading, startHref, startLabel } = useAppSession();
  const mobile = variant === "mobile";

  if (isLoading) {
    return (
      <div
        className={cn(
          "rounded-full bg-muted/80",
          mobile ? "h-12 w-full" : "h-10 w-40",
        )}
        aria-hidden
      />
    );
  }

  if (signedIn) {
    return (
      <Button
        size="lg"
        className={cn(
          "rounded-full px-6 text-base font-semibold",
          mobile && "w-full",
        )}
        asChild
      >
        <Link to={startHref} onClick={onNavigate}>
          {startLabel}
        </Link>
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size="lg"
        className={cn(
          "rounded-lg font-semibold text-foreground text-base",
          mobile ? "bg-muted" : "!bg-muted !px-6 py-2",
        )}
        asChild
      >
        <Link to={APP_LOGIN_URL} onClick={onNavigate}>
          Log in
        </Link>
      </Button>
      <Button
        size="lg"
        className={cn(
          "rounded-full px-6 text-base font-semibold",
          mobile && "w-full",
        )}
        asChild
      >
        <Link to={APP_REGISTER_URL} onClick={onNavigate}>
          {mobile ? "Start for free" : "Sign up free"}
        </Link>
      </Button>
    </>
  );
}

export function SiteHeader() {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);
  const [mobileSection, setMobileSection] = useState<MenuKey | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastY = useRef(0);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openMenu = (key: MenuKey) => {
    clearCloseTimer();
    setActiveMenu(key);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setActiveMenu(null), 140);
  };

  const closeMenus = () => {
    clearCloseTimer();
    setActiveMenu(null);
    setMobileSection(null);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    setHidden(false);
    lastY.current = window.scrollY;
  }, [pathname]);

  useEffect(() => {
    lastY.current = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY.current;
      lastY.current = y;

      if (mobileOpen) return;

      if (y < 16) {
        setHidden(false);
        return;
      }

      if (delta > 8) {
        setHidden(true);
        closeMenus();
      } else if (delta < -8) {
        setHidden(false);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mobileOpen]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-10 z-50 will-change-transform transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
        hidden && !mobileOpen
          ? "pointer-events-none -translate-y-[calc(100%+2.5rem)]"
          : "translate-y-0",
      )}
      aria-hidden={hidden && !mobileOpen}
    >
      <div
        className="relative mx-auto max-w-[80%]"
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleClose}
      >
        <div className="flex items-center justify-between gap-3 rounded-full bg-card px-4 py-2 xl:px-6 xl:py-4 ring-1 ring-black/[0.04]">
          <div className="flex items-center gap-10">
            <Link
              to="/"
              aria-label="Sqaleshop home"
              className="flex shrink-0 items-center pl-1 transition-opacity hover:opacity-80"
              onClick={() => {
                setMobileOpen(false);
                closeMenus();
              }}
            >
              <img src={Logo} alt="Sqaleshop" className="h-7 w-auto" />
            </Link>

            <nav className="hidden items-center gap-0.5 lg:flex">
              <NavTrigger
                label="Products"
                open={activeMenu === "product"}
                onHover={() => openMenu("product")}
              />
              <NavTrigger
                label="Solutions"
                open={activeMenu === "solutions"}
                onHover={() => openMenu("solutions")}
              />
              <NavLink
                to="/pricing"
                onMouseEnter={closeMenus}
                onFocus={closeMenus}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-4 py-2 text-base font-semibold tracking-tight transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-foreground/90 hover:bg-muted/60",
                  )
                }
              >
                Pricing
              </NavLink>
              <NavLink
                to="https://blog.sqaleshop.com"
                target="_blank"
                onMouseEnter={closeMenus}
                onFocus={closeMenus}
                className={({ isActive }) =>
                  cn(
                    "rounded-lg px-4 py-2 text-base font-semibold tracking-tight transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-foreground/90 hover:bg-muted/60",
                  )
                }
              >
                Learn
              </NavLink>
            </nav>
          </div>

          <div
            className="hidden items-center gap-2 lg:flex"
            onMouseEnter={closeMenus}
          >
            <HeaderAuthActions variant="desktop" />
          </div>

          <button
            type="button"
            className="inline-flex size-9 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted lg:hidden"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? (
              <RiCloseLine className="size-5" />
            ) : (
              <RiMenu3Line className="size-5" />
            )}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeMenu === "product" && (
            <ProductMegaMenu key="product" onNavigate={closeMenus} />
          )}
          {activeMenu === "solutions" && (
            <SolutionsMegaMenu key="solutions" onNavigate={closeMenus} />
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mx-10 mt-3 max-w-5xl overflow-hidden rounded-3xl border border-border bg-card shadow-xl ring-1 ring-black/[0.03] lg:hidden"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.24, ease: menuEase }}
          >
            <div className="p-2">
              {(["product", "solutions"] as const).map((key) => {
                const open = mobileSection === key;
                const label = key === "product" ? "Product" : "Solutions";
                const groups =
                  key === "product" ? productCategories : solutionsMenu;

                return (
                  <div key={key}>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold",
                        open ? "bg-muted" : "hover:bg-muted/60",
                      )}
                      onClick={() =>
                        setMobileSection((c) => (c === key ? null : key))
                      }
                    >
                      {label}
                      <motion.span
                        animate={{ rotate: open ? 180 : 0 }}
                        transition={{ duration: 0.22, ease: menuEase }}
                      >
                        <RiArrowDownSLine className="size-4" />
                      </motion.span>
                    </button>
                    <AnimatePresence>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.24, ease: menuEase }}
                          className="overflow-hidden"
                        >
                          <div className="space-y-3 px-1 py-2">
                            {groups.map((group) => (
                              <div key={group.id}>
                                <p className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-foreground">
                                  <group.Icon className="size-4 shrink-0" />
                                  {group.label}
                                </p>
                                <div className="ml-3 space-y-0.5 border-l border-border pl-2">
                                  {group.features.map((feature) => (
                                    <Link
                                      key={feature.title}
                                      to={feature.to}
                                      onClick={() => {
                                        setMobileOpen(false);
                                        closeMenus();
                                      }}
                                      className="group block rounded-lg px-3 py-2 transition-colors hover:bg-muted/60"
                                    >
                                      <span className="block text-sm font-semibold text-foreground group-hover:text-primary">
                                        {feature.title}
                                      </span>
                                      <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                                        {feature.description}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              <NavLink
                to="/pricing"
                className="block rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-muted/60"
                onClick={() => setMobileOpen(false)}
              >
                Pricing
              </NavLink>
              <NavLink
                to="https://blog.sqaleshop.com"
                target="_blank"
                className="block rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-muted/60"
                onClick={() => setMobileOpen(false)}
              >
                Learn
              </NavLink>

              <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
                <HeaderAuthActions
                  variant="mobile"
                  onNavigate={() => setMobileOpen(false)}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
